import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { createPlotlyChart } from 'src/app/_shared/plotlyConfig';
import { FleetGeneralFigureService } from 'src/app/_services/fleet-general-figure.service';
import { RbUiComponentsModule } from '@inst-iot/bosch-angular-ui-components';
import { VehicleGeneralFigureService } from 'src/app/_services/vehicle-general-figure.service';

@Component({
  selector: 'app-stress-factor-ef-chart',
  standalone: true,
  imports: [CommonModule, RbUiComponentsModule],
  templateUrl: './stress-factor-ef-chart.component.html',
  styleUrls: ['./stress-factor-ef-chart.component.scss'],
  host: { class: 'show-stress-factors-popover' }
})
export class StressFactorEfChartComponent implements OnChanges, OnDestroy {
  private readonly unsubscribe$ = new Subject<void>();
  @Input() selectedVehicles: any[] = [];
  @Input() fleetId: string[] = [];
  errorMessage: boolean = false;
  dataLoading: boolean = false;
  deviceSerialNumber!: any[];
  private colorMap: { [key: string]: string } = {};
  private colorMapBackground: { [key: string]: string } = {};

  constructor(
    private readonly fleetGeneralFigureService: FleetGeneralFigureService,
    private readonly vehicleGeneralFigureService: VehicleGeneralFigureService
  ) { }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.dataLoading = true;
    this.errorMessage = false;
    this.fleetGeneralFigureService.getStressFactorsFleet(this.fleetId).pipe(takeUntil(this.unsubscribe$)).subscribe(
      {
        next: (datapoints) => {
          const fleetData = this.plotStressFactorsFleet(datapoints);
          this.fetchVehicleInfoAndUpdateChart(fleetData);
        },
        error: (error) => {
          console.error('Error fetching Stress Factors Fleet data:', error);
          this.dataLoading = false;
          this.errorMessage = true;
        },
        complete: () => {
          this.dataLoading = false;
        }
      }
    )
  }

  plotStressFactorsFleet(datapoints: any) {
    const allPlotlyData: any[] = [];
    let fleetHasValid = false;

    Object.keys(datapoints).forEach((fleetId: string) => {
      const deviceData = datapoints[fleetId];

      ['Below', 'Average', 'Above'].forEach((group, idx) => {
        const factors = this.buildFleetFactors(deviceData, idx);
        const r = factors.map(f => f.value as number);
        const theta = factors.map(f => f.label);

        if (r.length > 0) {
          fleetHasValid = true;
          if (r.length > 1) {
            r.push(r[0]);
            theta.push(theta[0]);
          }

          let fillColor: string;
          let markerColor: string;
          if (idx === 0) {
            fillColor = '#C535BC80';
            markerColor = '#9E2896';
          } else if (idx === 1) {
            fillColor = '#007BC080';
            markerColor = '#00629A';
          } else {
            fillColor = '#00884A80';
            markerColor = '#006C3A';
          }

          allPlotlyData.push({
            type: 'scatterpolar',
            r,
            theta,
            fill: 'toself',
            fillcolor: fillColor,
            name: `Fleet (${group} Average Condition)`,
            marker: { color: markerColor }
          });
        }
      });
    });

    return { allPlotlyData, fleetHasValid };
  }

  private buildFleetFactors(deviceData: any, index: number): any[] {
    return [
      { value: deviceData.highTempValuePercent?.[index], label: 'High Battery<br> Temperature' },
      { value: deviceData.chargingLowTempValuePercent?.[index], label: 'Charging at<br>Low Temperature' },
      { value: deviceData.highSocValuePercent?.[index], label: 'Extreme<br>State of Charge' },
      { value: deviceData.lowSocValuePercent?.[index], label: 'Low<br> State of Charge' },
      { value: deviceData.fastChargingValuePercent?.[index], label: 'Share of Fast<br>Charging Session' }
    ].filter(factor => factor.value != null);
  }

  fetchVehicleInfoAndUpdateChart(fleetResult: { allPlotlyData: any[], fleetHasValid: boolean }): void {
    this.dataLoading = true;
    this.errorMessage = false;
    this.deviceSerialNumber = this.selectedVehicles.map(vehicle => vehicle.batteryIdentifier);
    this.vehicleGeneralFigureService.getStressFactors(this.deviceSerialNumber).pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (data: any) => {
        this.initializeColorMap();

        const allPlotlyData: any[] = [...fleetResult.allPlotlyData];
        let vehiclesHaveValid = false;

        Object.keys(data).forEach((deviceSerialNumberId: string) => {
          const deviceData = data[deviceSerialNumberId];
          const factors = this.buildVehicleFactors(deviceData);

          const r = factors.map(f => f.value as number);
          const theta = factors.map(f => f.label);

          if (r.length > 0) {
            vehiclesHaveValid = true;
            if (r.length > 1) {
              r.push(r[0]);
              theta.push(theta[0]);
            }
            const vehicle = this.selectedVehicles.find(vehicle => vehicle.batteryIdentifier === deviceSerialNumberId);
            allPlotlyData.push({
              type: 'scatterpolar',
              r,
              theta,
              fill: 'toself',
              name: vehicle ? vehicle.name : deviceSerialNumberId,
              fillcolor: this.colorMapBackground[deviceSerialNumberId],
              marker: {
                color: this.colorMap[deviceSerialNumberId]
              }
            });
          }
        });

        if (!fleetResult.fleetHasValid && !vehiclesHaveValid) {
          this.errorMessage = true;
          this.dataLoading = false;
          return;
        }

        this.updateChart(allPlotlyData);
        this.dataLoading = false;
      },
      error: (error) => {
        console.error('Error fetching Stress Factors data:', error);
        this.dataLoading = false;
        this.errorMessage = true;
      }
    });
  }

  private buildVehicleFactors(deviceData: any): any[] {
    return [
      { value: this.getVehiclePercent(deviceData, 'High Temperature', 'highTempValuePercent'), label: 'High Battery<br> Temperature' },
      { value: this.getVehiclePercent(deviceData, 'Low Temperature Charging', 'chargingLowTempValuePercent'), label: 'Charging at<br>Low Temperature' },
      { value: this.getVehiclePercent(deviceData, 'High State of Charge', 'highSocValuePercent'), label: 'Extreme<br>State of Charge' },
      { value: this.getVehiclePercent(deviceData, 'Low State of Charge', 'lowSocValuePercent'), label: 'Low<br> State of Charge' },
      { value: this.getVehiclePercent(deviceData, 'Fast Charging', 'fastChargingValuePercent'), label: 'Share of Fast<br>Charging Session' }
    ].filter(factor => factor.value != null);
  }

  private getVehiclePercent(deviceData: any, stressName: string, legacyField: string): number | null {
    if (deviceData?.stressFactors && Array.isArray(deviceData.stressFactors)) {
      const stressFactor = deviceData.stressFactors.find((factor: any) => factor.stressFactorName === stressName);
      return stressFactor ? stressFactor.value : null;
    }
    return deviceData ? deviceData[legacyField] : null;
  }

  updateChart(data: any[]): void {
    const layout = {
      title: 'Major Battery Stress Factors',
      font: {
        family: 'boschsans, Helvetica Neue, Helvetica, Arial, sans-serif',
        size: 13,
      },
      polar: {
        angularaxis: {
          rotation: 45,
        },
        radialaxis: {
          visible: true,
          range: [0, 100]
        }
      },
      showlegend: true,
      legend: {
        orientation: 'h',
        y: -0.3,
        xanchor: 'center',
        x: 0.5,
        font: {
          family: 'boschsans, Helvetica Neue, Helvetica, Arial, sans-serif',
          size: 13,
        },
      }
    }
    createPlotlyChart('stress-factors-ef-chart', data, layout);
    this.dataLoading = false;
  }

  initializeColorMap(): void {
    const colors = ['#007BC0', '#18837E','#9E2896'];
    this.selectedVehicles.forEach((vehicle, index) => {
      this.colorMap[vehicle.batteryIdentifier] = colors[index % colors.length];
    });
    const backgroundColors = ['#56B0FF80', '#66B8B280','#E552DA80'];
    this.selectedVehicles.forEach((vehicle, index) => {
      this.colorMapBackground[vehicle.batteryIdentifier] = backgroundColors[index % backgroundColors.length];
    });
  }
}
